<template>
  <div class="sign-canvas">
    <!-- 图片包装器 -->
    <div
      class="image-wrapper"
      ref="imageWrapper"
      @mousedown="onCanvasMouseDown"
      @mousemove="onCanvasMouseMove"
      @mouseup="onCanvasMouseUp"
    >
      <!-- 图片显示 -->
      <img
        class="canvas-image"
        ref="canvasImage"
        :src="page.imageUrl"
        @load="onImageLoad"
      />

      <!-- 区域层（覆盖在图片上） -->
      <div v-if="imageLoaded" class="regions-layer" ref="regionsLayer">
        <!-- 使用 RegionItem 组件渲染所有区域 -->
        <region-item
          v-for="region in regions"
          :key="region.id"
          :region="region"
          :selected="region.id === selectedRegionId"
          :editable="mode === 'edit'"
          :is-dragging="
            (isDragging || isResizing) && draggedRegionId === region.id
          "
          :container-size="{ width: containerWidth, height: containerHeight }"
          @mousedown="onRegionMouseDown($event, region)"
          @mouseup="onRegionMouseUp($event, region)"
          @delete="onDeleteRegion(region.id)"
          @resize-start="onResizeStartHandler($event, region)"
          @resize-end="onResizeEnd(region)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  REGION_TYPE_CONFIG,
  createRegion,
  clampPosition,
} from "../../constant";
import RegionItem from "../RegionItem/RegionItem.vue";

export default {
  name: "SignCanvas",

  components: {
    RegionItem,
  },

  props: {
    page: {
      type: Object,
      required: true,
    },
    regions: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: "edit", // edit | preview
    },
    activeTool: {
      type: String,
      default: "none",
    },
    selectedRegionId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      imageLoaded: false,
      imageWidth: 0,
      imageHeight: 0,
      containerWidth: 0,
      containerHeight: 0,

      // 拖拽状态
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragRegionStartX: 0,
      dragRegionStartY: 0,

      // 缩放状态
      isResizing: false,
      resizeDirection: "", // top, bottom, left, right
      resizeStartX: 0,
      resizeStartY: 0,
      resizeRegionStartX: 0,
      resizeRegionStartY: 0,
      resizeRegionStartWidth: 0,
      resizeRegionStartHeight: 0,
      cachedScreenWidth: 0, // 缓存屏幕宽度

      // 节流相关
      lastMoveTime: 0,
      pendingUpdate: null,
      rafId: null, // requestAnimationFrame ID
      pendingPosition: null, // 待更新的位置

      // 点击与拖抽区分
      mouseStartTime: 0,
      hasMoved: false,
      draggedRegionId: null,

      // 画布点击状态 (PC端使用mouse)
      canvasMouseStartX: 0,
      canvasMouseStartY: 0,
      canvasMouseStartTime: 0,
      canvasHasMoved: false,

      // 画布级别句柄检测（点击画布时检测距离哪个句柄近）
      canvasResizeMode: false,
      canvasResizeDirection: "",
      regionsLayerRect: null, // 缓存的 regions-layer 位置

      // 当前拖拽/缩放的区域ID（使用ID而不是引用，避免Vue响应式问题）
      activeRegionId: null,
    };
  },

  computed: {},

  mounted() {
    // 绑定 document 级别的鼠标事件，解决拖拽时鼠标离开元素的问题
    document.addEventListener("mousemove", this.onDocumentMouseMove);
    document.addEventListener("mouseup", this.onDocumentMouseUp);
  },

  beforeDestroy() {
    // 移除 document 级别的鼠标事件
    document.removeEventListener("mousemove", this.onDocumentMouseMove);
    document.removeEventListener("mouseup", this.onDocumentMouseUp);
  },

  methods: {
    // 图片加载完成 (PC端)
    onImageLoad(e) {
      var img = e.target;
      this.imageWidth = img.naturalWidth;
      this.imageHeight = img.naturalHeight;

      // 获取图片实际尺寸
      this.$nextTick(() => {
        var imgEl = this.$refs.canvasImage;
        if (imgEl) {
          var rect = imgEl.getBoundingClientRect();
          this.containerWidth = rect.width;
          this.containerHeight = rect.height;
          this.imageLoaded = true;
        }
      });
    },

    // 更新容器尺寸（用于拖拽和缩放计算）(PC端)
    updateContainerSize() {
      var layerEl = this.$refs.regionsLayer;
      if (layerEl) {
        var rect = layerEl.getBoundingClientRect();
        this.containerWidth = rect.width;
        this.containerHeight = rect.height;
        this.regionsLayerRect = rect;
      }
    },

    // 画布鼠标按下 (PC端)
    onCanvasMouseDown(e) {
      this.canvasMouseStartX = e.clientX;
      this.canvasMouseStartY = e.clientY;
      this.canvasMouseStartTime = Date.now();
      this.canvasHasMoved = false;

      // 重置画布级别缩放状态
      this.canvasResizeMode = false;
      this.canvasResizeDirection = "";

      // 获取 regions-layer 位置用于检测
      this.updateContainerSize();

      // 检测是否点击在选中区域的句柄附近
      if (this.mode === "edit" && this.selectedRegionId) {
        var direction = this.detectNearestHandle(e.clientX, e.clientY);
        if (direction) {
          this.canvasResizeMode = true;
          this.canvasResizeDirection = direction;
          this.onResizeStart(
            { clientX: e.clientX, clientY: e.clientY },
            this.getSelectedRegion(),
            direction
          );
        }
      }
    },

    // 获取选中的区域
    getSelectedRegion() {
      if (!this.selectedRegionId) return null;
      return this.regions.find((r) => r.id === this.selectedRegionId);
    },

    // 检测触摸点距离哪个句柄最近
    // 返回方向 ('top', 'bottom', 'left', 'right') 或 null
    detectNearestHandle(touchX, touchY) {
      var region = this.getSelectedRegion();
      if (!region || region.type === "text") return null;
      if (!this.containerWidth || !this.containerHeight) return null;

      var pos = region.position;
      // 计算区域在屏幕上的实际位置（需要加上 regions-layer 的偏移）
      var regionLeft = pos.x * this.containerWidth;
      var regionTop = pos.y * this.containerHeight;
      var regionRight = regionLeft + pos.width * this.containerWidth;
      var regionBottom = regionTop + pos.height * this.containerHeight;
      var regionCenterX = (regionLeft + regionRight) / 2;
      var regionCenterY = (regionTop + regionBottom) / 2;

      // 获取 regions-layer 的位置偏移
      // 由于是同步计算，使用缓存的位置（在 updateContainerSize 时更新）
      var offsetX = 0;
      var offsetY = 0;
      if (this.regionsLayerRect) {
        offsetX = this.regionsLayerRect.left;
        offsetY = this.regionsLayerRect.top;
      }

      // 计算四个句柄的中心位置（相对于屏幕）
      var handleTop = { x: regionCenterX + offsetX, y: regionTop + offsetY };
      var handleBottom = {
        x: regionCenterX + offsetX,
        y: regionBottom + offsetY,
      };
      var handleLeft = { x: regionLeft + offsetX, y: regionCenterY + offsetY };
      var handleRight = {
        x: regionRight + offsetX,
        y: regionCenterY + offsetY,
      };

      // 计算触摸点到四个句柄的距离
      var distTop = Math.sqrt(
        Math.pow(touchX - handleTop.x, 2) + Math.pow(touchY - handleTop.y, 2)
      );
      var distBottom = Math.sqrt(
        Math.pow(touchX - handleBottom.x, 2) +
          Math.pow(touchY - handleBottom.y, 2)
      );
      var distLeft = Math.sqrt(
        Math.pow(touchX - handleLeft.x, 2) + Math.pow(touchY - handleLeft.y, 2)
      );
      var distRight = Math.sqrt(
        Math.pow(touchX - handleRight.x, 2) +
          Math.pow(touchY - handleRight.y, 2)
      );

      // 找出最小距离
      var minDist = Math.min(distTop, distBottom, distLeft, distRight);

      // 阈值：30px 以内认为是点击句柄附近
      var threshold = 30;

      if (minDist < threshold) {
        if (minDist === distTop) return "top";
        if (minDist === distBottom) return "bottom";
        if (minDist === distLeft) return "left";
        if (minDist === distRight) return "right";
      }

      return null;
    },

    // 画布鼠标移动 (PC端)
    onCanvasMouseMove(e) {
      // 如果是画布级别缩放模式，处理缩放
      if (this.canvasResizeMode && this.canvasResizeDirection) {
        this.onResizeMove(
          { clientX: e.clientX, clientY: e.clientY },
          this.getSelectedRegion(),
          this.canvasResizeDirection
        );
        this.canvasHasMoved = true;
        return;
      }

      // 检测是否有明显移动（超过 10px 认为是滑动）
      var deltaX = Math.abs(e.clientX - this.canvasMouseStartX);
      var deltaY = Math.abs(e.clientY - this.canvasMouseStartY);
      if (deltaX > 10 || deltaY > 10) {
        this.canvasHasMoved = true;
      }
    },

    // 画布鼠标抬起 (PC端)
    onCanvasMouseUp(e) {
      // 如果是画布级别缩放模式，结束缩放
      if (this.canvasResizeMode && this.canvasResizeDirection) {
        this.onResizeEnd();
        this.canvasResizeMode = false;
        this.canvasResizeDirection = "";
        return;
      }

      var clickDuration = Date.now() - this.canvasMouseStartTime;
      var isClick = !this.canvasHasMoved && clickDuration < 300;

      if (!isClick) {
        return;
      }

      // 如果不是编辑模式或没有选择工具，取消选中
      if (this.mode !== "edit" || this.activeTool === "none") {
        if (this.selectedRegionId) {
          this.$emit("region-select", null);
        }
        return;
      }

      // 计算点击位置的百分比坐标，创建新区域 (PC端)
      var layerEl = this.$refs.regionsLayer;
      if (layerEl) {
        var rect = layerEl.getBoundingClientRect();
        var x = (e.clientX - rect.left) / rect.width;
        var y = (e.clientY - rect.top) / rect.height;

        // 创建新区域
        var config = REGION_TYPE_CONFIG[this.activeTool];
        var newRegion = createRegion(
          this.activeTool,
          {
            x: Math.max(
              0,
              Math.min(x - config.defaultWidth / 2, 1 - config.defaultWidth)
            ),
            y: Math.max(
              0,
              Math.min(y - config.defaultHeight / 2, 1 - config.defaultHeight)
            ),
          },
          this.page.pageId
        );

        this.$emit("region-add", newRegion);
      }
    },

    // 区域鼠标按下 (PC端)
    onRegionMouseDown(e, region) {
      if (this.mode !== "edit") return;

      // 记录鼠标开始时间和状态
      this.mouseStartTime = Date.now();
      this.hasMoved = false;
      this.draggedRegionId = region.id;
      this.activeRegionId = region.id; // 保存当前拖拽的区域ID

      // 开始拖拽
      this.isDragging = true;
      this.$emit("drag-start");
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragRegionStartX = region.position.x;
      this.dragRegionStartY = region.position.y;

      // 获取最新的容器尺寸
      this.updateContainerSize();
    },

    // 区域鼠标抬起 (PC端) - 必须在这里处理，因为 .stop 修饰符阻止了事件冒泡到 document
    onRegionMouseUp(e, region) {
      // 处理拖抽结束
      if (this.isDragging && this.activeRegionId) {
        this.handleDragEnd(e);
        return;
      }
      // 处理缩放结束
      if (this.isResizing && this.activeRegionId) {
        var activeRegion = this.getActiveRegion();
        this.onResizeEnd(activeRegion);
      }
    },

    // 根据ID获取当前操作的区域
    getActiveRegion() {
      if (!this.activeRegionId) return null;
      return this.regions.find((r) => r.id === this.activeRegionId) || null;
    },

    // document 级别鼠标移动事件 - 解决拖拽时鼠标离开元素的问题
    onDocumentMouseMove(e) {
      // 非编辑模式不处理
      if (this.mode !== "edit") return;

      var region = null;

      // 处理拖拽
      if (this.isDragging && this.activeRegionId) {
        region = this.getActiveRegion();
        if (region) {
          this.handleDragMove(e, region);
        }
        return;
      }
      // 处理缩放
      if (this.isResizing && this.activeRegionId) {
        region = this.getActiveRegion();
        if (region) {
          this.onResizeMove(
            { clientX: e.clientX, clientY: e.clientY },
            region,
            this.resizeDirection
          );
        }
        return;
      }
      // 处理画布级别缩放
      if (this.canvasResizeMode && this.canvasResizeDirection) {
        this.onResizeMove(
          { clientX: e.clientX, clientY: e.clientY },
          this.getSelectedRegion(),
          this.canvasResizeDirection
        );
        this.canvasHasMoved = true;
      }
    },

    // document 级别鼠标抬起事件 - 处理鼠标在元素外部松开的情况
    onDocumentMouseUp(e) {
      // 始终清理拖拽状态，不管模式如何
      // 处理拖拽结束
      if (this.isDragging) {
        if (this.activeRegionId) {
          this.handleDragEnd(e);
        } else {
          // 异常情况：强制重置状态
          this.isDragging = false;
          this.draggedRegionId = null;
          this.$emit("drag-end");
        }
        return;
      }
      // 处理缩放结束
      if (this.isResizing) {
        var region = this.getActiveRegion();
        this.onResizeEnd(region);
        return;
      }
      // 处理画布级别缩放结束
      if (this.canvasResizeMode && this.canvasResizeDirection) {
        this.onResizeEnd();
        this.canvasResizeMode = false;
        this.canvasResizeDirection = "";
      }
    },

    // 拖拽移动处理 - 直接更新，不使用复杂的缓存机制
    handleDragMove(e, region) {
      if (!this.containerWidth || !this.containerHeight) return;
      if (!region) return;

      var deltaX = (e.clientX - this.dragStartX) / this.containerWidth;
      var deltaY = (e.clientY - this.dragStartY) / this.containerHeight;

      // 检测是否有明显移动（超过 5px 认为是拖拽）
      var pixelDeltaX = Math.abs(e.clientX - this.dragStartX);
      var pixelDeltaY = Math.abs(e.clientY - this.dragStartY);
      if (pixelDeltaX > 5 || pixelDeltaY > 5) {
        this.hasMoved = true;
      }

      var newX = this.dragRegionStartX + deltaX;
      var newY = this.dragRegionStartY + deltaY;

      // 获取用于边界限制的宽高（文本区需要获取实际渲染尺寸）
      var boundWidth = region.position.width;
      var boundHeight = region.position.height;

      if (region.type === "text") {
        // 文本区 height 为 auto，需要获取实际渲染高度
        var regionEl = this.$refs.regionsLayer?.querySelector(
          '[data-region-id="' + region.id + '"]'
        );
        if (regionEl) {
          var rect = regionEl.getBoundingClientRect();
          boundHeight = rect.height / this.containerHeight;
        }
      }

      // 边界限制
      if (newX < 0) newX = 0;
      if (newY < 0) newY = 0;
      if (newX + boundWidth > 1) newX = 1 - boundWidth;
      if (newY + boundHeight > 1) newY = 1 - boundHeight;

      // 直接更新位置
      this.$emit("region-update", {
        ...region,
        position: {
          ...region.position,
          x: newX,
          y: newY,
        },
      });
    },

    // 拖拽结束处理
    handleDragEnd(e) {
      var clickDuration = Date.now() - this.mouseStartTime;
      var isClick = !this.hasMoved && clickDuration < 500;
      this.$emit("region-select", {
        regionId: this.activeRegionId,
        isClick: isClick,
      });
      this.isDragging = false;
      this.draggedRegionId = null;
      this.activeRegionId = null;
      this.$emit("drag-end");
    },

    // 缩放开始 (PC端)
    onResizeStart(e, region, direction) {
      // PC端直接使用 e.clientX/clientY
      var clientX = e.clientX;
      var clientY = e.clientY;

      this.isDragging = false;
      this.isResizing = true;
      this.draggedRegionId = region.id;
      this.activeRegionId = region.id; // 保存当前缩放的区域ID
      this.$emit("drag-start");
      this.resizeDirection = direction;
      this.resizeStartX = clientX;
      this.resizeStartY = clientY;
      this.resizeRegionStartX = region.position.x;
      this.resizeRegionStartY = region.position.y;
      this.resizeRegionStartWidth = region.position.width;
      this.resizeRegionStartHeight = region.position.height;

      // PC端使用 window.innerWidth
      this.cachedScreenWidth = window.innerWidth;

      this.updateContainerSize();
    },

    // 缩放移动 (PC端)
    onResizeMove(e, region, direction) {
      if (!this.isResizing) return;
      if (!this.containerWidth || !this.containerHeight) return;

      var clientX = e.clientX;
      var clientY = e.clientY;
      var deltaX = (clientX - this.resizeStartX) / this.containerWidth;
      var deltaY = (clientY - this.resizeStartY) / this.containerHeight;

      var config = REGION_TYPE_CONFIG[region.type];
      var minWidth = config.minWidth / this.containerWidth;
      var minHeight = config.minHeight / this.containerHeight;

      // PC端直接使用 px
      var fontSize = region.config.fontSize || 24;
      var fontSizePx = fontSize; // 直接使用 fontSize 作为 px 值

      // 对于文本区和输入区，最小高度基于字体大小
      if (region.type === "text" || region.type === "input") {
        var minHeightPx = fontSizePx + 4;
        var fontBasedMinHeight = minHeightPx / this.containerHeight;
        minHeight = Math.max(minHeight, fontBasedMinHeight);
      }

      var newX = this.resizeRegionStartX;
      var newY = this.resizeRegionStartY;
      var newWidth = this.resizeRegionStartWidth;
      var newHeight = this.resizeRegionStartHeight;

      // 根据方向调整
      if (this.resizeDirection === "top") {
        // 上句柄：等比例缩放（上下同时变化，宽度不变）
        // deltaY > 0 表示向下拖动（缩小），deltaY < 0 表示向上拖动（放大）
        // 计算高度变化量（上下各变化 deltaY 的一半，总共变化 deltaY * 2）
        var heightDelta = -deltaY * 2; // 向上拖动增加高度
        var newHeightCandidate = this.resizeRegionStartHeight + heightDelta;

        // 确保不小于最小高度
        if (newHeightCandidate < minHeight) {
          heightDelta = minHeight - this.resizeRegionStartHeight;
          newHeightCandidate = minHeight;
        }

        // 计算新的 y 位置（中心点不变，上下同时扩展）
        var yDelta = -heightDelta / 2;
        var newYCandidate = this.resizeRegionStartY + yDelta;

        // 边界检查：不能超出顶部
        if (newYCandidate < 0) {
          newYCandidate = 0;
          newHeightCandidate =
            this.resizeRegionStartY +
            this.resizeRegionStartHeight -
            newYCandidate;
        }

        // 边界检查：不能超出底部
        if (newYCandidate + newHeightCandidate > 1) {
          newHeightCandidate = 1 - newYCandidate;
        }

        newY = newYCandidate;
        newHeight = newHeightCandidate;
      } else if (this.resizeDirection === "bottom") {
        // 下句柄：单向缩放（只改变下边框，上边框位置不动）
        newHeight = this.resizeRegionStartHeight + deltaY;
        newHeight = Math.max(minHeight, newHeight);
        // 不能超出底部边界
        if (newY + newHeight > 1) {
          newHeight = 1 - newY;
        }
      } else if (this.resizeDirection === "left") {
        // 向左拖动：x减小，width增加
        var maxLeftDelta = this.resizeRegionStartX; // 最多拖到左边
        var maxRightDelta = this.resizeRegionStartWidth - minWidth; // 最小宽度限制
        deltaX = Math.max(-maxLeftDelta, Math.min(deltaX, maxRightDelta));
        newX = this.resizeRegionStartX + deltaX;
        newWidth = this.resizeRegionStartWidth - deltaX;
      } else if (this.resizeDirection === "right") {
        // 向右拖动：width增加
        newWidth = this.resizeRegionStartWidth + deltaX;
        newWidth = Math.max(minWidth, newWidth);
        // 不能超出右边界
        if (newX + newWidth > 1) {
          newWidth = 1 - newX;
        }
      }

      // 输入区：特殊逻辑
      // 1. 上下拉最小高度不能小于一行文字高度，但可以变大
      // 2. 左右拉最小宽度是文字一行的宽度
      if (region.type === "input") {
        var placeholder = region.config.placeholder || "";
        if (placeholder.length > 0) {
          // 估算文字总宽度（中文字符宽度约等于字体大小，增加安全边距）
          var textWidthPx = placeholder.length * fontSizePx * 1.1; // 增加10%安全边距
          // PC端 padding + 边框
          var paddingPx = 8;
          // 计算行高（字体大小 * 1.2）
          var lineHeightPx = fontSizePx * 1.2;

          // 左右拉：最小宽度 = 文字一行的宽度 + padding
          var oneLineMinWidthPx = textWidthPx + paddingPx;
          var oneLineMinWidth = oneLineMinWidthPx / this.containerWidth;
          // 限制最小宽度不能小于一行文字宽度
          if (newWidth < oneLineMinWidth) {
            newWidth = oneLineMinWidth;
            // 如果是左句柄拖动，需要调整 x 位置
            if (this.resizeDirection === "left") {
              newX =
                this.resizeRegionStartX +
                this.resizeRegionStartWidth -
                newWidth;
              if (newX < 0) {
                newX = 0;
                newWidth =
                  this.resizeRegionStartX + this.resizeRegionStartWidth;
              }
            }
          }

          // 当前区域的实际像素宽度
          var regionWidthPx = newWidth * this.containerWidth - paddingPx;
          if (regionWidthPx < fontSizePx) regionWidthPx = fontSizePx;

          // 计算文字需要的最小高度（一行文字高度）
          var oneLineMinHeightPx = lineHeightPx + paddingPx;
          var oneLineMinHeight = oneLineMinHeightPx / this.containerHeight;

          // 上下拉：最小高度不能小于一行文字高度，但可以变大
          if (newHeight < oneLineMinHeight) {
            newHeight = oneLineMinHeight;
            // 如果是上句柄，调整 y 位置
            if (this.resizeDirection === "top") {
              newY =
                this.resizeRegionStartY +
                this.resizeRegionStartHeight -
                newHeight;
              if (newY < 0) {
                newY = 0;
              }
            }
          }

          // 左右拉时，确保高度不小于最小高度
          if (
            this.resizeDirection === "left" ||
            this.resizeDirection === "right"
          ) {
            if (newHeight < oneLineMinHeight) {
              newHeight = oneLineMinHeight;
            }
          }

          // 边界检查
          if (newY + newHeight > 1) {
            newHeight = 1 - newY;
          }
        }
      }

      this.$emit("region-update", {
        ...region,
        position: {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        },
      });
    },

    // 缩放结束 (PC端)
    onResizeEnd(region) {
      this.isResizing = false;
      this.isDragging = false;
      this.draggedRegionId = null;
      this.activeRegionId = null;
      this.$emit("drag-end");

      // 如果是文本区，调整高度以适应内容
      if (region && region.type === "text") {
        this.adjustTextRegionHeight(region);
      }
    },

    // 调整文本区高度以适应内容 (PC端)
    adjustTextRegionHeight(region) {
      if (!region || region.type !== "text") return;
      if (!this.containerWidth || !this.containerHeight) return;

      var content = region.config.content || "";
      if (!content) return;

      var fontSize = region.config.fontSize || 24;
      // PC端直接使用 px
      var fontSizePx = fontSize / 2;

      // 计算区域的实际像素宽度
      var regionWidthPx = region.position.width * this.containerWidth;

      // 估算每行能容纳的字符数（中文字符宽度约等于字体大小）
      var charsPerLine = Math.floor(regionWidthPx / fontSizePx);
      if (charsPerLine < 1) charsPerLine = 1;

      // 计算需要的行数
      var lines = Math.ceil(content.length / charsPerLine);

      // 计算需要的高度（行数 * 行高）
      var lineHeight = fontSizePx * 1.2;
      var neededHeightPx = lines * lineHeight;

      // 转换为百分比
      var neededHeight = neededHeightPx / this.containerHeight;

      // 获取最小高度（根据配置）
      var minHeightPx = 30; // 最小高度30px
      var minHeight = minHeightPx / this.containerHeight;

      // 确保不小于最小高度
      neededHeight = Math.max(neededHeight, minHeight);

      // 确保不超出底部边界
      var maxHeight = 1 - region.position.y;
      neededHeight = Math.min(neededHeight, maxHeight);

      // 只有当高度发生变化时才更新
      if (Math.abs(neededHeight - region.position.height) > 0.001) {
        this.$emit("region-update", {
          ...region,
          position: {
            ...region.position,
            height: neededHeight,
          },
        });
      }
    },

    // 删除区域
    onDeleteRegion(regionId) {
      this.$emit("region-delete", regionId);
    },

    // 处理来自 RegionItem 的缩放开始事件
    onResizeStartHandler(data, region) {
      // data 包含 { event, direction }
      if (data && data.event && data.direction) {
        this.onResizeStart(data.event, region, data.direction);
      }
    },

    // 获取容器尺寸（供父组件调用）
    getContainerSize() {
      return {
        width: this.containerWidth,
        height: this.containerHeight,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.sign-canvas {
  width: 100%;
  min-height: 100%;
  padding: 10px;
  box-sizing: border-box;
  background-color: #f6f6f6;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.image-wrapper {
  position: relative;
  width: 100%;
  max-width: 800px;
}

.canvas-image {
  width: 100%;
  display: block;
  background-color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.regions-layer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
